import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, Send, FileLock2, ShieldCheck, Clock, BadgeCheck, CalendarDays } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";
import CTABanner from "@/components/CTABanner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FAQS } from "@/data/content";

const SERVICES_OPTIONS = [
  "AI & Business Automation",
  "Custom Software Development",
  "Web & Mobile Apps",
  "UI/UX Design & Branding",
  "Digital Marketing & SEO",
  "Cloud & Consulting",
  "Product Demo (LMS / CRM / School)",
  "Something else",
];

const inputCls =
  "w-full bg-white border border-slate-200 rounded-2xl px-5 py-4 text-sm text-[#01193D] placeholder:text-slate-400 focus:outline-none focus:border-[#2563EB] focus:ring-4 focus:ring-[#2563EB]/10 transition-all";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Message sent! We'll get back to you within one business day.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    }, 900);
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative pt-36 pb-16 px-5 sm:px-8 overflow-hidden" data-testid="contact-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CAF2F9]/30 to-white" />
        <div className="absolute -top-24 left-1/3 w-96 h-96 rounded-full bg-[#91E0F1]/20 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionLabel>Contact</SectionLabel>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#01193D] leading-[1.1]">
              Let's start a <span className="text-gradient-electric">conversation</span>
            </h1>
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-6 max-w-xl mx-auto">
              Tell us about your project, your product idea, or the problem keeping you up at night. First consultation is always free.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SPLIT LAYOUT */}
      <section className="px-5 sm:px-8 pb-20" data-testid="contact-split-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl border border-slate-100 soft-shadow p-8 sm:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-name" className="block text-sm font-medium text-[#01193D] mb-2">Name *</label>
                  <input id="c-name" data-testid="contact-name-input" className={inputCls} placeholder="Jane Smith" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label htmlFor="c-email" className="block text-sm font-medium text-[#01193D] mb-2">Email *</label>
                  <input id="c-email" type="email" data-testid="contact-email-input" className={inputCls} placeholder="jane@company.com" value={form.email} onChange={set("email")} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-company" className="block text-sm font-medium text-[#01193D] mb-2">Company</label>
                  <input id="c-company" data-testid="contact-company-input" className={inputCls} placeholder="Acme Inc." value={form.company} onChange={set("company")} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#01193D] mb-2">Service Interested In</label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger data-testid="contact-service-select" className="w-full !h-[52px] bg-white border-slate-200 rounded-2xl px-5 text-sm">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label htmlFor="c-message" className="block text-sm font-medium text-[#01193D] mb-2">Message *</label>
                <textarea id="c-message" data-testid="contact-message-input" rows={5} className={`${inputCls} resize-none`} placeholder="Tell us about your project…" value={form.message} onChange={set("message")} />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="btn-gradient w-full sm:w-auto text-white font-semibold px-9 py-4 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"} <Send size={17} />
              </button>
              <div className="flex flex-wrap gap-2.5 pt-2" data-testid="contact-trust-badges">
                {[
                  { Icon: Clock, label: "24-Hour Response" },
                  { Icon: FileLock2, label: "NDA on Request" },
                  { Icon: BadgeCheck, label: "Free Initial Consultation" },
                  { Icon: ShieldCheck, label: "GDPR-Ready" },
                ].map(({ Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#01193D] bg-[#CAF2F9]/70 border border-[#91E0F1]/40 px-3.5 py-1.5 rounded-full">
                    <Icon size={13} className="text-[#2563EB]" /> {label}
                  </span>
                ))}
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2 space-y-5">
            <div className="bg-[#01193D] rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#2563EB]/25 blur-2xl" />
              <h3 className="font-display font-semibold text-xl text-white relative">Contact Details</h3>
              <div className="space-y-5 mt-7 relative">
                {[
                  { Icon: Mail, label: "Email", value: "hello@theaurgelabs.com", testid: "contact-detail-email" },
                  { Icon: Phone, label: "Phone", value: "+1 (555) 012-3456", testid: "contact-detail-phone" },
                  { Icon: MapPin, label: "Office", value: "100 Innovation Drive, Suite 400\nSan Francisco, CA 94105", testid: "contact-detail-address" },
                ].map(({ Icon, label, value, testid }) => (
                  <div key={label} className="flex gap-4" data-testid={testid}>
                    <span className="shrink-0 w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
                      <Icon size={18} className="text-[#09B2ED]" />
                    </span>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
                      <p className="text-sm text-white mt-1 whitespace-pre-line">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-8 relative">
                {[Linkedin, Twitter, Instagram].map((Icon, i) => (
                  <a key={i} href="#social" aria-label="Social link" className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white hover:bg-[#2563EB] transition-colors">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden border border-slate-100 soft-shadow" data-testid="contact-map">
              <iframe
                title="The Aurge Labs office location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-122.4194%2C37.7699%2C-122.3894%2C37.7949&layer=mapnik&marker=37.7824%2C-122.4044"
                className="w-full h-64 grayscale-[30%] contrast-[1.05]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 sm:px-8 py-16 lg:py-24 bg-[#F8FAFC]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D]">
              Questions we hear a lot
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="bg-white rounded-2xl border border-slate-100 soft-shadow px-6 data-[state=open]:border-[#2563EB]/40"
                >
                  <AccordionTrigger className="font-display font-semibold text-[#01193D] text-left hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#475569] text-sm leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* BOOK A CALL */}
      <section className="px-5 sm:px-8 py-16" data-testid="book-call-section">
        <Reveal className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl border border-slate-100 soft-shadow p-8 sm:p-12 flex flex-col sm:flex-row items-center gap-8 text-center sm:text-left">
            <span className="shrink-0 w-16 h-16 rounded-3xl bg-gradient-to-br from-[#2563EB] to-[#09B2ED] flex items-center justify-center">
              <CalendarDays size={28} className="text-white" />
            </span>
            <div className="flex-1">
              <h2 className="font-display font-bold text-2xl text-[#01193D]">Prefer to book a call directly?</h2>
              <p className="text-[#475569] text-sm mt-2">Skip the form — grab a free 30-minute slot with a senior team member and talk it through live.</p>
            </div>
            <a
              href="mailto:hello@theaurgelabs.com?subject=Book%20a%2030-min%20call"
              data-testid="book-call-button"
              className="btn-gradient shrink-0 text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2"
            >
              Book a 30-Min Call <CalendarDays size={16} />
            </a>
          </div>
        </Reveal>
      </section>

      <CTABanner title="Prefer to just say hello?" subtitle="Drop us a line anytime — we answer every message within one business day." cta="Email hello@theaurgelabs.com" />
    </div>
  );
}
